import React, {useState} from 'react';
import {ChevronDown, ChevronUp, CreditCard} from 'lucide-react';
import apiClient from '@/shared/services/apiClient';
import Button from '@/shared/components/Button';

interface GiftCardFieldProps {
    onApply: (code: string, balanceCents: number) => void;
    onClear: () => void;
    appliedCode: string | null;
    appliedAmount: number;
}

const GiftCardField: React.FC<GiftCardFieldProps> = ({onApply, onClear, appliedCode, appliedAmount}) => {
    const [isOpen, setIsOpen] = useState(!!appliedCode);
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleApply = async () => {
        if (!code.trim()) return;
        setError('');
        setLoading(true);
        try {
            const result = await apiClient.post<{valid: boolean; balanceCents: number; code: string}>(
                '/api/shop/gift-card/validate',
                {code: code.trim()}
            );
            onApply(result.code, result.balanceCents);
            setCode('');
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Invalid gift card';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 text-sm text-mld-text-primary font-medium hover:underline">
                <CreditCard className="h-4 w-4" />
                Pay with a Gift Card
                {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>

            {isOpen && (
                <div className="mt-2">
                    {appliedCode ? (
                        <div className="flex items-center justify-between bg-mld-success/10 rounded-lg px-3 py-2">
                            <span className="text-sm text-mld-text-primary font-medium">
                                {appliedCode} – ${(appliedAmount / 100).toFixed(2)} applied
                            </span>
                            <button type="button" onClick={onClear} className="text-xs text-mld-danger hover:underline">
                                Remove
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                                    placeholder="Enter gift card code"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-mld-success/50"
                                />
                                <Button
                                    type="button"
                                    variant="primary"
                                    size="md"
                                    onClick={handleApply}
                                    disabled={!code.trim()}
                                    loading={loading}>
                                    Apply
                                </Button>
                            </div>
                            {error && <p className="text-xs text-mld-danger mt-1">{error}</p>}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default GiftCardField;
